import React, { useState } from 'react';

interface IntroductionCourseProps {
  onComplete: () => void;
}

type GoalType = 'pronunciation' | 'interview' | 'ielts' | null;

const IntroductionCourse: React.FC<IntroductionCourseProps> = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState<GoalType>(null);

  const totalSteps = 4;

  const nextStep = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  // Progress dots
  const ProgressDots = () => (
    <div className="flex justify-center gap-2 mt-8">
      {Array.from({ length: totalSteps }).map((_, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full transition-all duration-300 ${
            i === step
              ? 'w-6 bg-blue-600'
              : i < step
              ? 'bg-blue-400'
              : 'bg-gray-300'
          }`}
        />
      ))}
    </div>
  );

  // Step 1: Welcome
  const WelcomeStep = () => (
    <div className="text-center px-6">
      <img
        src="/whale-happy.png"
        alt="Welcome to Litrlly!"
        className="w-32 h-32 mx-auto mb-6 object-contain drop-shadow-lg"
      />
      <h1 className="text-3xl font-bold text-gray-800 mb-4">
        Welcome to Litrlly!
      </h1>
      <p className="text-gray-600 text-lg mb-6 max-w-md mx-auto">
        Your personal AI-powered coach for improving pronunciation, acing interviews, and mastering IELTS.
      </p>
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          AI-powered feedback
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Practice anytime
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Track your progress
        </div>
      </div>
    </div>
  );

  // Step 2: Goal Selection
  const GoalSelectionStep = () => {
    const goals = [
      {
        id: 'pronunciation' as GoalType,
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        ),
        title: 'Better Pronunciation',
        description: 'Improve clarity and accent',
        color: 'blue',
      },
      {
        id: 'interview' as GoalType,
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        ),
        title: 'Interview Prep',
        description: 'Ace your job interviews',
        color: 'purple',
      },
      {
        id: 'ielts' as GoalType,
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        ),
        title: 'IELTS Exam',
        description: 'Prepare for the IELTS test',
        color: 'emerald',
      },
    ];

    const getColorClasses = (color: string, isSelected: boolean) => {
      const colors: Record<string, { bg: string; border: string; icon: string }> = {
        blue: {
          bg: isSelected ? 'bg-blue-100' : 'bg-blue-50',
          border: isSelected ? 'border-blue-500' : 'border-transparent',
          icon: 'text-blue-600',
        },
        purple: {
          bg: isSelected ? 'bg-purple-100' : 'bg-purple-50',
          border: isSelected ? 'border-purple-500' : 'border-transparent',
          icon: 'text-purple-600',
        },
        emerald: {
          bg: isSelected ? 'bg-emerald-100' : 'bg-emerald-50',
          border: isSelected ? 'border-emerald-500' : 'border-transparent',
          icon: 'text-emerald-600',
        },
      };
      return colors[color];
    };

    return (
      <div className="text-center px-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          What's your main goal?
        </h2>
        <p className="text-gray-600 mb-8">
          Don't worry, you can practice all modes anytime!
        </p>
        <div className="flex flex-col gap-4 max-w-sm mx-auto">
          {goals.map((goal) => {
            const isSelected = selectedGoal === goal.id;
            const colors = getColorClasses(goal.color, isSelected);
            return (
              <button
                key={goal.id}
                onClick={() => setSelectedGoal(goal.id)}
                className={`${colors.bg} ${colors.border} border-2 rounded-xl p-4 flex items-center gap-4 transition-all duration-200 hover:scale-102`}
              >
                <div className={`${colors.icon}`}>{goal.icon}</div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-800">{goal.title}</h3>
                  <p className="text-sm text-gray-500">{goal.description}</p>
                </div>
                {isSelected && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 ml-auto text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Step 3: How It Works
  const HowItWorksStep = () => {
    const steps = [
      {
        number: 1,
        title: 'Choose your text',
        description: 'Select from our library or enter your own',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      },
      {
        number: 2,
        title: 'Record yourself',
        description: 'Speak clearly into your microphone',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        ),
      },
      {
        number: 3,
        title: 'Get instant feedback',
        description: 'AI analyzes pronunciation and fluency',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        ),
      },
      {
        number: 4,
        title: 'Improve & repeat',
        description: 'Practice makes perfect!',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ),
      },
    ];

    return (
      <div className="text-center px-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          How it works
        </h2>
        <p className="text-gray-600 mb-8">
          Simple steps to improve your speaking
        </p>
        <div className="max-w-sm mx-auto space-y-4">
          {steps.map((s, idx) => (
            <div
              key={s.number}
              className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm"
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600">{s.icon}</span>
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-800">{s.title}</h3>
                <p className="text-sm text-gray-500">{s.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Step 4: Ready to Start
  const ReadyStep = () => (
    <div className="text-center px-6">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">
        You're all set!
      </h2>
      <p className="text-gray-600 mb-8 max-w-md mx-auto">
        Start practicing now and track your progress over time. Remember, consistency is key!
      </p>
      <div className="bg-blue-50 rounded-xl p-6 max-w-sm mx-auto mb-6">
        <h3 className="font-semibold text-blue-800 mb-3">Free Plan Includes:</h3>
        <ul className="text-left space-y-2">
          <li className="flex items-center gap-2 text-blue-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            5 recordings per day
          </li>
          <li className="flex items-center gap-2 text-blue-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            All practice modes
          </li>
          <li className="flex items-center gap-2 text-blue-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            AI-powered feedback
          </li>
        </ul>
      </div>
      <p className="text-sm text-gray-500">
        Upgrade anytime for unlimited recordings
      </p>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 0:
        return <WelcomeStep />;
      case 1:
        return <GoalSelectionStep />;
      case 2:
        return <HowItWorksStep />;
      case 3:
        return <ReadyStep />;
      default:
        return <WelcomeStep />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
      {/* Skip button */}
      <div className="flex justify-end p-4">
        <button
          onClick={onComplete}
          className="text-gray-400 hover:text-gray-600 text-sm font-medium"
        >
          Skip
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col justify-center pb-20">
        {renderStep()}
        <ProgressDots />
      </div>

      {/* Navigation buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4">
        <div className="max-w-md mx-auto flex gap-4">
          {step > 0 && (
            <button
              onClick={prevStep}
              className="flex-1 py-3 px-6 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
          )}
          <button
            onClick={nextStep}
            disabled={step === 1 && !selectedGoal}
            className={`flex-1 py-3 px-6 rounded-xl font-medium transition-colors ${
              step === 1 && !selectedGoal
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {step === totalSteps - 1 ? "Let's Go!" : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntroductionCourse;
